import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS, PaginatedSuggestionList, Suggestion } from '@/types/api';

export interface GetSuggestionsParams {
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface Recommendation {
  id: number;
  suggestion: number;
  suggestion_title?: string;
  title: string;
  description: string;
  recommendation_type: string;
  primary_link?: string;
  price_min?: number;
  price_max?: number;
  currency?: string;
  provider?: string;
  relevance_score?: number;
  clicked: boolean;
  clicked_at?: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
}

export interface Followup {
  id: number;
  suggestion: number;
  suggestion_title: string;
  followup_type: string;
  content: string;
  ai_confidence?: number;
  is_read: boolean;
  read_at?: string;
  created_at: string;
}

export const suggestionsApi = {
  // Get all suggestions for the authenticated user
  list: async (params?: GetSuggestionsParams): Promise<PaginatedSuggestionList> => {
    return apiClient.get<PaginatedSuggestionList>(API_ENDPOINTS.suggestions.list, params);
  },

  // Get a specific suggestion
  get: async (id: number): Promise<Suggestion> => {
    return apiClient.get<Suggestion>(API_ENDPOINTS.suggestions.detail(id));
  },

  // Get recommendations for a suggestion
  getRecommendations: async (suggestionId: number): Promise<Recommendation[]> => {
    const response = await apiClient.get<any>(`${API_ENDPOINTS.suggestions.detail(suggestionId)}recommendations/`);
    return response.results || response;
  },

  // Get followups for a suggestion
  getFollowups: async (suggestionId: number): Promise<Followup[]> => {
    const response = await apiClient.get<any>(`${API_ENDPOINTS.suggestions.detail(suggestionId)}followups/`);
    return response.results || response;
  },

  // Complete a suggestion
  complete: async (id: number, notes?: string): Promise<Suggestion> => {
    return apiClient.post<Suggestion>(API_ENDPOINTS.suggestions.complete(id), {
      completion_notes: notes || ''
    });
  },

  // Dismiss a suggestion
  dismiss: async (id: number): Promise<Suggestion> => {
    return apiClient.post<Suggestion>(API_ENDPOINTS.suggestions.dismiss(id), {});
  },

  // Get upcoming scheduled suggestions
  getUpcoming: async (days?: number): Promise<Suggestion[]> => {
    const params = days ? { days } : undefined;
    return apiClient.get<Suggestion[]>('/api/suggestions/upcoming/', params);
  },

  // Get all followups for the authenticated user
  getAllFollowups: async (params?: { ordering?: string; page?: number; page_size?: number }): Promise<{ results: Followup[]; count: number }> => {
    return apiClient.get<{ results: Followup[]; count: number }>('/api/suggestion-followups/', params);
  },

  // Mark a followup as read
  markFollowupAsRead: async (followupId: number): Promise<Followup> => {
    return apiClient.post<Followup>(`/api/suggestion-followups/${followupId}/mark_as_read/`, {});
  },

  // Mark all followups as read
  markAllFollowupsAsRead: async (): Promise<{ message: string; count: number }> => {
    return apiClient.post<{ message: string; count: number }>('/api/suggestion-followups/mark_all_as_read/', {});
  },

  // Get all recommendations for the authenticated user
  getAllRecommendations: async (params?: { ordering?: string; page?: number; page_size?: number }): Promise<{ results: Recommendation[]; count: number }> => {
    return apiClient.get<{ results: Recommendation[]; count: number }>('/api/recommendations/', params);
  },

  // Track recommendation interaction (click or purchase)
  trackRecommendation: async (recommendationId: number, action: 'click' | 'purchase', commission?: number): Promise<{ message: string; clicked: boolean; purchased: boolean }> => {
    return apiClient.post<{ message: string; clicked: boolean; purchased: boolean }>(`/api/recommendations/${recommendationId}/track/`, { action, commission });
  },

  // Mark a recommendation as read
  markRecommendationAsRead: async (recommendationId: number): Promise<Recommendation> => {
    return apiClient.post<Recommendation>(`/api/recommendations/${recommendationId}/mark_as_read/`, {});
  },

  // Mark all recommendations as read
  markAllRecommendationsAsRead: async (): Promise<{ message: string; count: number }> => {
    return apiClient.post<{ message: string; count: number }>('/api/recommendations/mark_all_as_read/', {});
  },
};
